import React, { useMemo } from 'react';
import axios from 'axios';
import { getDriverApiBaseUrl } from '../../utils/utils';
import { formatMoney, formatDateTime, Pill, EmptyState, WalletSpinner } from './walletShared';

// ── Per-AWB "chain of custody" API layer ────────────────────────────────────
// All endpoints are relative to the driver backend base which ends in
// ".../shypvdriverapp/". Envelope is {success,data,errors}; callers read
// response.data.data. Auth is Authorization: Bearer <usertoken>.

export const custodyApiBase = () => `${getDriverApiBaseUrl()}/shypvdriverapp/`;

export const custodyAuthHeaders = () => {
    const t = sessionStorage.getItem('usertoken');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

// Pulls the {success,data} envelope apart and returns data (defaulting to []).
const unwrap = (res, fallback) => (res && res.data && res.data.data != null ? res.data.data : fallback);

// Prefer errorDetails (the specific reason, e.g. "AWB X is not held by you") over errorMessage,
// which is the generic enum default (e.g. "Please provide the accurate data.").
const errMsg = (res, fallback) =>
    (res && res.data && res.data.errors && res.data.errors.map(e => e.errorDetails || e.errorMessage || e).join(', ')) || fallback;

// ── Stage metadata (Driver → Supervisor → Account → Company) ────────────────
export const CUSTODY_STAGES = ['DRIVER', 'SUPERVISOR', 'ACCOUNT', 'COMPANY'];

export const STAGE_META = {
    DRIVER: { label: 'Driver', icon: 'fa-truck', tone: 'warning' },
    SUPERVISOR: { label: 'Supervisor', icon: 'fa-user-secret', tone: 'primary' },
    ACCOUNT: { label: 'Account', icon: 'fa-user-tie', tone: 'primary' },
    COMPANY: { label: 'Company', icon: 'fa-university', tone: 'success' },
};

export const stageMeta = (stage) => STAGE_META[stage] || { label: stage || '-', icon: 'fa-question-circle', tone: 'neutral' };

// Timestamp on a CustodyAwb that best represents when it reached a given stage.
const STAGE_TIMESTAMP_FIELD = {
    DRIVER: 'collectedAt',
    SUPERVISOR: 'supervisorAt',
    ACCOUNT: 'accountAt',
    COMPANY: 'companyAt',
};

export const stageTimestamp = (awb, stage) => (awb ? awb[STAGE_TIMESTAMP_FIELD[stage]] : null);

// ── Reads ───────────────────────────────────────────────────────────────────
export const fetchDriverHeld = async (driverId) => {
    const res = await axios.get(`${custodyApiBase()}admin/wallet/custody/driver/${driverId}/held`, { headers: custodyAuthHeaders() });
    return unwrap(res, []);
};

// Driver's run-sheet AWBs, optionally scoped to a date (YYYY-MM-DD). When `date`
// is empty the param is omitted so the backend returns AWBs across all dates.
export const fetchDriverRunsheet = async (driverId, date) => {
    const res = await axios.get(`${custodyApiBase()}admin/wallet/custody/driver/${driverId}/runsheet`, {
        params: date ? { date } : {},
        headers: custodyAuthHeaders(),
    });
    return unwrap(res, []);
};

export const fetchSupervisorHeld = async (userId) => {
    const res = await axios.get(`${custodyApiBase()}admin/wallet/custody/supervisor/${userId}/held`, { headers: custodyAuthHeaders() });
    return unwrap(res, []);
};

export const fetchAccountHeld = async (userId) => {
    const res = await axios.get(`${custodyApiBase()}admin/wallet/custody/account/${userId}/held`, { headers: custodyAuthHeaders() });
    return unwrap(res, []);
};

export const fetchAwbTrail = async (awb) => {
    const res = await axios.get(`${custodyApiBase()}admin/wallet/custody/awb/${encodeURIComponent(awb)}/trail`, { headers: custodyAuthHeaders() });
    return unwrap(res, null);
};

// Whole COD custody ledger — every AWB, amount, current stage and who holds the cash.
export const fetchLedger = async () => {
    const email = sessionStorage.getItem('useremail') || '';
    const res = await axios.get(`${custodyApiBase()}admin/wallet/custody/ledger`, {
        params: email ? { email } : {},
        headers: custodyAuthHeaders(),
    });
    return unwrap(res, []);
};

// ── Hand-offs ───────────────────────────────────────────────────────────────
// Each returns { ok, result?, error? } where result is a CustodyHandoffResult.
const postHandoff = async (path, body) => {
    try {
        const res = await axios.post(`${custodyApiBase()}${path}`, body,
            { headers: { ...custodyAuthHeaders(), 'Content-Type': 'application/json' } });
        if (res.data && res.data.success) return { ok: true, result: res.data.data };
        return { ok: false, error: errMsg(res, 'Hand-off failed.') };
    } catch (err) {
        console.error(err);
        return {
            ok: false,
            error: errMsg(err.response, 'Hand-off failed. Please try again.'),
        };
    }
};

export const handoffDriverToSupervisor = ({ awbs, driverId, signature, notes, email }) =>
    postHandoff('admin/wallet/custody/driver-to-supervisor', { awbs, fromPartyId: driverId, signature, notes, email });

export const handoffSupervisorToAccount = ({ awbs, supervisorUserId, signature, notes, email }) =>
    postHandoff('admin/wallet/custody/supervisor-to-account', { awbs, fromPartyId: supervisorUserId, signature, notes, email });

export const handoffAccountToCompany = ({ awbs, signature, notes, email }) =>
    postHandoff('admin/wallet/custody/account-to-company', { awbs, signature, notes, email });

// ── Currency helper: the first non-empty currency across a list of CustodyAwbs ──
export const custodyCurrency = (list, fallback = 'AED') =>
    ((list || []).find(a => a && a.currency) || {}).currency || fallback;

// ── Checklist table of held AWBs with select-all + a live SUM ─────────────────
// Whole-AWB selection only (no partial amounts). `selected` is a Set of awb strings.
export function AwbChecklist({ items, selected, onToggle, onToggleAll, loading, emptyTitle, emptySubtitle }) {
    const allSelected = items.length > 0 && items.every(a => selected.has(a.awb));
    const someSelected = items.some(a => selected.has(a.awb)) && !allSelected;

    if (loading) return <WalletSpinner label="Loading held AWBs…" />;
    if (!items.length) {
        return <EmptyState icon="fa-inbox" title={emptyTitle || 'No AWBs held at this stage'} subtitle={emptySubtitle} />;
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm align-middle" style={{ marginTop: 4, marginBottom: 0 }}>
                <thead>
                    <tr>
                        <th style={{ width: 42 }}>
                            <input
                                type="checkbox"
                                aria-label="Select all AWBs"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected; }}
                                onChange={onToggleAll}
                            />
                        </th>
                        <th>AWB</th>
                        <th className="text-end">COD</th>
                        <th className="text-end">Duty/Tax</th>
                        <th className="text-end">Total</th>
                        <th>Collected at</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(a => {
                        const isSel = selected.has(a.awb);
                        return (
                            <tr key={a.awb} style={isSel ? { background: '#f2f9ff' } : undefined}>
                                <td>
                                    <input
                                        type="checkbox"
                                        aria-label={`Select ${a.awb}`}
                                        checked={isSel}
                                        onChange={() => onToggle(a.awb)}
                                    />
                                </td>
                                <td><span className="w-id-chip">{a.awb}</span></td>
                                <td className="text-end">{formatMoney(a.codAmount, a.currency)}</td>
                                <td className="text-end">{formatMoney(a.dutiesTaxAmount, a.currency)}</td>
                                <td className="text-end" style={{ fontWeight: 600 }}>{formatMoney(a.amount, a.currency)}</td>
                                <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12.5 }}>{formatDateTime(a.collectedAt)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// COD / Duty-Tax / Total sums across the currently-selected AWBs.
export const useSelectedTotals = (items, selected) => useMemo(
    () => (items || []).reduce((acc, a) => {
        if (!selected.has(a.awb)) return acc;
        acc.cod += Number(a.codAmount) || 0;
        acc.dutiesTax += Number(a.dutiesTaxAmount) || 0;
        acc.total += Number(a.amount) || 0;
        return acc;
    }, { cod: 0, dutiesTax: 0, total: 0 }),
    [items, selected]
);

// ── Vertical timeline of custody steps (Driver → Supervisor → Account → Company) ──
export function CustodyTimeline({ steps }) {
    if (!steps || !steps.length) {
        return <EmptyState icon="fa-clock-o" title="No hand-off steps recorded yet" />;
    }
    return (
        <div className="w-custody-timeline">
            {steps.map((s, i) => {
                const to = stageMeta(s.toStage);
                const from = stageMeta(s.fromStage);
                return (
                    <div className="w-ct-row" key={s.handoffId != null ? s.handoffId : i}>
                        <div className="w-ct-marker">
                            <span className={`w-ct-dot w-ct-dot-${to.tone}`}><i className={`fa ${to.icon}`} aria-hidden="true" /></span>
                            {i < steps.length - 1 ? <span className="w-ct-line" /> : null}
                        </div>
                        <div className="w-ct-body">
                            <div className="w-ct-head">
                                <span className="w-ct-stages">
                                    <Pill tone={from.tone} icon={from.icon}>{from.label}</Pill>
                                    <i className="fa fa-long-arrow-right" aria-hidden="true" style={{ color: '#9aa1ac', margin: '0 2px' }} />
                                    <Pill tone={to.tone} icon={to.icon}>{to.label}</Pill>
                                </span>
                                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <strong>{formatMoney(s.amount)}</strong>
                                    {s.signed
                                        ? <Pill tone="success" icon="fa-check">Signed</Pill>
                                        : <Pill tone="neutral" icon="fa-pencil">Unsigned</Pill>}
                                </span>
                            </div>
                            <div className="w-ct-meta">
                                <span><i className="fa fa-user" aria-hidden="true" /> {s.collectedByEmail || s.collectedByUserId || '—'}</span>
                                <span><i className="fa fa-clock-o" aria-hidden="true" /> {formatDateTime(s.createdAt)}</span>
                                {s.notes ? <span><i className="fa fa-sticky-note-o" aria-hidden="true" /> {s.notes}</span> : null}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
